import { useEffect, useState } from "react";
import { CheckCircle2, Mail, Phone, XCircle } from "lucide-react";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AgentService } from "@/services/agent/core";
import type { InactiveGenie } from "@/types/admin/inactive-genies";

const DELETE_CONCURRENCY = 3;

interface DeleteFailure {
  genie: InactiveGenie;
  message: string;
}

interface DeleteInactiveGeniesSheetProps {
  genies: InactiveGenie[];
  isOpen: boolean;
  onClose: () => void;
  /** Called once the run finishes, with the ids that were deleted. */
  onDeleted: (deletedIds: string[]) => void;
}

/**
 * Confirms and runs a bulk delete of inactive genies. Each genie goes through
 * the regular genie delete (phone number release, ElevenLabs, mailbox, HG Sync),
 * a few at a time, and the sheet reports what failed.
 */
export const DeleteInactiveGeniesSheet = ({ genies, isOpen, onClose, onDeleted }: DeleteInactiveGeniesSheetProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [done, setDone] = useState(0);
  const [deletedCount, setDeletedCount] = useState<number | null>(null);
  const [failures, setFailures] = useState<DeleteFailure[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setDone(0);
    setDeletedCount(null);
    setFailures([]);
  }, [isOpen]);

  const withPhone = genies.filter((g) => g.has_phone).length;
  const withMailbox = genies.filter((g) => g.has_mailbox).length;
  const isFinished = deletedCount !== null;

  const handleDelete = async () => {
    setIsDeleting(true);
    const deletedIds: string[] = [];
    const failed: DeleteFailure[] = [];
    const queue = [...genies];

    const worker = async () => {
      for (let genie = queue.shift(); genie; genie = queue.shift()) {
        try {
          await AgentService.deleteAgent(null, genie.id);
          deletedIds.push(genie.id);
        } catch (error) {
          failed.push({ genie, message: error instanceof Error ? error.message : "Delete failed" });
        }
        setDone((n) => n + 1);
      }
    };
    await Promise.all(Array.from({ length: Math.min(DELETE_CONCURRENCY, genies.length) }, worker));

    setFailures(failed);
    setDeletedCount(deletedIds.length);
    setIsDeleting(false);
    onDeleted(deletedIds);
  };

  const count = genies.length;
  const plural = count === 1 ? "genie" : "genies";

  const footer = (
    <div className="flex w-full justify-start gap-3 border-t pt-4">
      {isFinished ? (
        <Button onClick={onClose} className="min-h-[44px]">
          Done
        </Button>
      ) : (
        <>
          <Button variant="outline" onClick={onClose} disabled={isDeleting} className="min-h-[44px]">
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isDeleting || count === 0} className="min-h-[44px] px-6">
            {isDeleting ? `Deleting ${done} of ${count}...` : `Delete ${count} ${plural}`}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title={isFinished ? "Bulk delete finished" : `Delete ${count} inactive ${plural}`}
      description={
        isFinished
          ? "Deleted genies are gone for good, along with their phone numbers and mailboxes."
          : "This can't be undone. Each genie is deleted the same way its owner would delete it."
      }
      size="lg"
      footer={footer}
      preventOutsideClick={isDeleting}
      showCloseButton={!isDeleting}
    >
      <div className="space-y-6">
        {isDeleting && (
          <div className="space-y-2">
            <Progress value={(done / Math.max(count, 1)) * 100} className="h-2" />
            <p className="text-sm text-gray-600">
              {done} of {count} done. Keep this sheet open until it finishes.
            </p>
          </div>
        )}

        {isFinished && (
          <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="rounded-[10px] bg-white p-5 flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-genie-primary flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-gray-900">{deletedCount}</span> deleted
                {failures.length > 0 && (
                  <>
                    , <span className="font-semibold text-red-600">{failures.length}</span> failed
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {failures.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Not deleted</p>
            <ul className="space-y-2">
              {failures.map(({ genie, message }) => (
                <li key={genie.id} className="flex items-start gap-2 text-sm">
                  <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="font-medium text-gray-900">{genie.name}</span>
                    <span className="block text-xs text-gray-500 break-words">{message}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!isFinished && !isDeleting && (withPhone > 0 || withMailbox > 0) && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-1 text-sm text-amber-800">
            {withPhone > 0 && (
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 flex-shrink-0" />
                {withPhone} {withPhone === 1 ? "has a phone number" : "have phone numbers"} that will be released
              </p>
            )}
            {withMailbox > 0 && (
              <p className="flex items-center gap-2">
                <Mail className="h-4 w-4 flex-shrink-0" />
                {withMailbox} {withMailbox === 1 ? "has a mailbox" : "have mailboxes"} that will be deleted
              </p>
            )}
          </div>
        )}

        {!isFinished && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Genies to delete</p>
            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
              {genies.map((genie) => (
                <li key={genie.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-900 truncate">{genie.name}</span>
                    <span className="block text-xs text-gray-500 truncate">
                      {genie.owner_email ?? "No owner"}
                    </span>
                  </span>
                  <span className="flex items-center gap-1.5 text-gray-400 flex-shrink-0">
                    {genie.has_phone && <Phone className="h-3.5 w-3.5" aria-label="Has a phone number" />}
                    {genie.has_mailbox && <Mail className="h-3.5 w-3.5" aria-label="Has a mailbox" />}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </BaseSheet>
  );
};
